import React, { useState, useEffect, lazy, Suspense } from 'react';
import PasswordScreen from './components/PasswordScreen';
import OfflineDetector from './components/OfflineDetector';

// Lazy load the birthday content - it will ONLY be loaded after authentication
const BirthdayContent = lazy(() => import('./components/BirthdayContent'));

const App = () => {
  const [language, setLanguage] = useState('de');
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => localStorage.getItem('birthday_token') !== null
  );
  const [translations, setTranslations] = useState(null);
  const [loadingTranslations, setLoadingTranslations] = useState(false);

  // Minimal translations for password screen (only what's needed before authentication)
  const passwordTranslations = {
    de: {
      switchLang: 'English',
      passwordTitle: 'Passwort erforderlich',
      passwordSubtitle: 'Bitte gib das Passwort ein, um die Einladung zu sehen',
      passwordPlaceholder: 'Passwort eingeben',
      passwordSubmit: 'Login',
      passwordSubmitting: 'Wird überprüft...',
      passwordError: 'Falsches Passwort. Bitte versuche es erneut.',
      privacyTitle: 'Datenschutzhinweis',
      privacyText: 'Mit deiner Anmeldung erfassen wir deinen Namen, E-Mail-Adresse, Teilnahmebestätigung, Alkoholpräferenz und optionale Angaben (mitgebrachte Speisen, Allergien/Ernährungseinschränkungen, zusätzliche Informationen). Diese Daten werden ausschliesslich zur Planung und Organisation der Geburtstagsfeier verwendet. Deine Daten werden bis zwei Wochen nach der Veranstaltung gespeichert und anschliessend gelöscht. Zugriff haben nur die Organisatoren der Feier. Du kannst jederzeit die Löschung deiner Daten verlangen, indem du dich an admin@sarinakaegi.ch wendest. Die Datenerfassung erfolgt in Übereinstimmung mit dem schweizerischen Datenschutzgesetz (DSG) und der DSGVO.',
      privacyShowMore: 'Datenschutzhinweis anzeigen',
      privacyShowLess: 'Datenschutzhinweis ausblenden'
    },
    en: {
      switchLang: 'Deutsch',
      passwordTitle: 'Password Required',
      passwordSubtitle: 'Please enter the password to view the invitation',
      passwordPlaceholder: 'Enter password',
      passwordSubmit: 'Login',
      passwordSubmitting: 'Verifying...',
      passwordError: 'Incorrect password. Please try again.',
      privacyTitle: 'Privacy Notice',
      privacyText: 'By registering, we collect your name, email address, attendance confirmation, alcohol preference, and optional information (food contributions, allergies/dietary restrictions, additional information). This data is used exclusively for planning and organizing the birthday party. Your data will be stored until two weeks after the event and then deleted. Only the event organizers have access. You can request deletion of your data at any time by contacting admin@sarinakaegi.ch. Data collection complies with the Swiss Federal Act on Data Protection (FADP) and GDPR.',
      privacyShowMore: 'Show privacy notice',
      privacyShowLess: 'Hide privacy notice'
    }
  };

  const handleLanguageToggle = () => {
    setLanguage(language === 'de' ? 'en' : 'de');
  };

  const handleAuthenticated = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('birthday_token');
    setIsAuthenticated(false);
    setTranslations(null);
  };

  // Fetch event details and translations after authentication
  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!isAuthenticated) return;

      setLoadingTranslations(true);
      try {
        const token = localStorage.getItem('birthday_token');
        const apiBaseUrl = process.env.REACT_APP_API_ENDPOINT
          ? process.env.REACT_APP_API_ENDPOINT.replace('/rsvps', '')
          : 'https://sarinakaegi.ch/api';
        const response = await fetch(`${apiBaseUrl}/event-details`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 401) {
          handleLogout();
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch event details');
        }

        const eventDetails = await response.json();
        setTranslations(eventDetails);
      } catch (error) {
        console.error('Error fetching event details:', error);
      } finally {
        setLoadingTranslations(false);
      }
    };

    fetchEventDetails();
  }, [isAuthenticated]);

  // Show password screen if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <PasswordScreen
          onAuthenticated={handleAuthenticated}
          translations={passwordTranslations}
          language={language}
          onLanguageChange={handleLanguageToggle}
        />
        <OfflineDetector language={language} />
      </>
    );
  }

  // Show loading state while fetching translations
  if (loadingTranslations || !translations) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
          <div className="text-purple-600 text-xl">Loading...</div>
        </div>
        <OfflineDetector language={language} />
      </>
    );
  }

  // Show main content only AFTER authentication AND translations are loaded
  // Using Suspense with lazy loading ensures the component isn't even loaded until needed
  return (
    <>
      <Suspense fallback={
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
          <div className="text-purple-600 text-xl">Loading...</div>
        </div>
      }>
        <BirthdayContent
          translations={translations}
          language={language}
          onLanguageChange={handleLanguageToggle}
          onLogout={handleLogout}
        />
      </Suspense>
      <OfflineDetector language={language} />
    </>
  );
};

export default App;
